import React from "react";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import {
  Row,
  Col,
  Button,
  Container,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip
} from "reactstrap";
import { faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import { divideArrayInSubArrays } from "../../../../src/utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import s from "./styles.scss";
import { setChildStep } from "../../helpers/stepper-state-comparator";
import forEach from "lodash.foreach";
import IAction from "../../../types/IAction";
import {
  MemoTable,
  ComisionLimit
} from "./SalesComissionsSportsProgramConfig.table";
import FormBuilderModal from "../common/formBuilderModal/formBuilderModal";
import {
  formConfigFactory,
  Inputs
} from "./SalesComissionsSportsProgramConfig.form";
import {
  validateDuplicateLimit,
  LimitErrorType,
  hasAnyError
} from "./SalesComissionsSportsProgramConfig.validations";
import { errorNotification } from "../../../utils/notifications";

interface Props {
  productId: string;
  saveComissions: (body: any) => IAction;
  autorizeComissions: (id: string, tipo: string) => IAction;
  comissions: any;
  catalogPostions: any;
}

interface State {
  rows: ComisionLimit[];
  selectedPuestos: { [key: string]: boolean };
  load: boolean;
  modalOpen: boolean;
  editingIndex: number | null;
  editingLimit: ComisionLimit | null;
  tipoMonto: "PORCENTAJE" | "MONETARIO";
  calculatedDesde: number;
  formValuesSetter: (values: any) => void;
}

class SalesComissionsSportsProgramConfig extends React.Component<Props, State> {
  state: State = {
    rows: [],
    selectedPuestos: {},
    load: false,
    modalOpen: false,
    editingIndex: null,
    editingLimit: null,
    tipoMonto: "PORCENTAJE",
    calculatedDesde: 1,
    formValuesSetter: null as (values: any) => void
  };

  componentDidUpdate(prevProps) {
    // Cargar comisiones cuando cambian
    if (this.props.comissions.comisiones !== prevProps.comissions.comisiones) {
      const { comisiones } = this.props.comissions;

      // Determinar el tipo global basado en el primer límite
      const tipoMonto =
        comisiones.length > 0 && comisiones[0].montoPorcentajeParticipante > 0
          ? "PORCENTAJE"
          : "MONETARIO";

      // Cargar los límites del servidor
      const updatedComissions = comisiones.map((row, index) => {
        let numeroParticipantesDesde = row.numeroParticipantesDesde;

        // Si el servidor no envió el "Desde", calcularlo
        if (!numeroParticipantesDesde) {
          if (index === 0) {
            numeroParticipantesDesde = 1;
          } else {
            const prevLimit = comisiones[index - 1];
            numeroParticipantesDesde = prevLimit.numeroParticipantesHasta + 1;
          }
        }

        return {
          numeroParticipantesDesde,
          numeroParticipantesHasta: row.numeroParticipantesHasta,
          montoPorParticipante: row.montoPorParticipante || 0,
          montoPorcentajeParticipante: row.montoPorcentajeParticipante || 0
        };
      });

      // Ordenar los límites al cargar del servidor
      this.setState({
        rows: this.sortAndValidateLimits(updatedComissions),
        tipoMonto
      });
    }

    // Cargar puestos seleccionados cuando cambian
    if (
      this.props.comissions !== null &&
      this.props.comissions.puestos &&
      this.props.catalogPostions &&
      !this.state.load
    ) {
      const puestosExistentes = this.props.comissions.puestos;
      const selectedPuestos = {
        ...this.state.selectedPuestos
      };

      puestosExistentes.forEach(puestoId => {
        this.props.catalogPostions.forEach(puesto => {
          if (puesto.positionId === puestoId) {
            selectedPuestos[puesto.positionId] = true;
          }
        });
      });

      setChildStep(selectedPuestos);
      this.setState(state => ({ ...state, selectedPuestos, load: true }));
    }
  }

  cleanState = state => {
    const newChildState = {};
    forEach(state, (value, key) => {
      if (value) {
        newChildState[key] = value;
      }
    });

    return newChildState;
  };

  checkPuesto = (e, positionId) => {
    const checked = e.target.checked;
    const selectedPuestos = {
      ...this.state.selectedPuestos,
      [positionId]: checked
    };

    setChildStep(this.cleanState(selectedPuestos));

    this.setState(state => ({
      ...state,
      selectedPuestos
    }));
  };

  selectAll = (e, value) => {
    e.preventDefault();
    const selectedPuestos = { ...this.state.selectedPuestos };

    this.props.catalogPostions.forEach(({ positionId }) => {
      selectedPuestos[positionId] = value;
    });

    setChildStep(this.cleanState(selectedPuestos));

    this.setState(state => ({
      ...state,
      selectedPuestos
    }));
  };

  openAddModal = () => {
    const { rows } = this.state;
    // Calcular el "Desde" para el nuevo límite
    const limiteDesde =
      rows.length === 0
        ? 1
        : rows[rows.length - 1].numeroParticipantesHasta + 1;

    this.setState({
      modalOpen: true,
      editingIndex: null,
      editingLimit: null,
      calculatedDesde: limiteDesde
    });
  };

  openEditModal = (index: number, limit: ComisionLimit) => {
    // Usar el valor "Desde" original del límite, no recalcularlo
    // Esto permite mostrar errores en el modal
    const calculatedDesde = limit.numeroParticipantesDesde;

    this.setState({
      modalOpen: true,
      editingIndex: index,
      editingLimit: limit,
      calculatedDesde
    });
  };

  closeModal = () => {
    this.setState({
      modalOpen: false,
      editingIndex: null,
      editingLimit: null
    });
  };

  deleteLimit = (index: number) => {
    const { rows } = this.state;
    const newRows = rows.filter((_, i) => i !== index);
    this.setState({ rows: this.sortAndValidateLimits(newRows) });
  };

  sortAndValidateLimits = (limits: ComisionLimit[]): ComisionLimit[] => {
    // Separar límites con fin (-1 = sin límite) y sin fin
    const withLimit = limits.filter(l => l.numeroParticipantesHasta !== -1);
    const withoutLimit = limits.filter(l => l.numeroParticipantesHasta === -1);

    // Ordenar los que tienen límite
    const sorted = [...withLimit].sort(
      (a, b) => a.numeroParticipantesHasta - b.numeroParticipantesHasta
    );

    // Recalcular los "desde" para que sean consecutivos
    const recalculated = sorted.map((limit, i) => ({
      ...limit,
      numeroParticipantesDesde:
        i === 0 ? 1 : sorted[i - 1].numeroParticipantesHasta + 1
    }));

    // Agregar los sin límite al final
    return [
      ...recalculated,
      ...withoutLimit.map(limit => ({
        ...limit,
        numeroParticipantesDesde:
          recalculated.length > 0
            ? recalculated[recalculated.length - 1].numeroParticipantesHasta + 1
            : 1
      }))
    ];
  };

  validateLimitForm = (values: any): string | null => {
    const limiteUsuarios = values.sinLimite
      ? -1
      : parseInt(values.limiteUsuarios);
    const monto = parseFloat(values.monto);
    const { tipoMonto, rows, editingIndex } = this.state;

    // Validar que el límite no esté duplicado
    const [duplicateError, duplicateMessage] = validateDuplicateLimit(
      limiteUsuarios,
      rows,
      editingIndex
    );
    if (duplicateError !== LimitErrorType.NONE) {
      return duplicateMessage;
    }

    // Validar que el monto no sea negativo
    if (monto < 0) {
      return "El monto no puede ser negativo";
    }

    // Validar que el monto no sea 0
    if (monto === 0) {
      return tipoMonto === "PORCENTAJE"
        ? "El porcentaje de comisión no puede ser 0%"
        : "El monto de comisión no puede ser $0";
    }

    return null;
  };

  saveLimit = async (values: any) => {
    const error = this.validateLimitForm(values);
    if (error) {
      errorNotification(error);
      throw new Error(error);
    }

    const limiteUsuarios = values.sinLimite
      ? -1
      : parseInt(values.limiteUsuarios);
    const monto = parseFloat(values.monto);
    const { tipoMonto } = this.state; // Usar el tipo del estado, no del formulario

    // Validar porcentaje si es tipo PORCENTAJE
    if (tipoMonto === "PORCENTAJE" && monto > 100) {
      errorNotification("El porcentaje no puede ser mayor a 100%");
      throw new Error("El porcentaje no puede ser mayor a 100%");
    }

    const newLimit: ComisionLimit = {
      numeroParticipantesDesde: this.state.calculatedDesde,
      numeroParticipantesHasta: limiteUsuarios,
      montoPorParticipante: tipoMonto === "MONETARIO" ? monto : 0,
      montoPorcentajeParticipante: tipoMonto === "PORCENTAJE" ? monto : 0
    };

    const { rows, editingIndex } = this.state;
    let newRows: ComisionLimit[];

    if (editingIndex !== null) {
      // Editar existente
      newRows = rows.map((row, i) => (i === editingIndex ? newLimit : row));
    } else {
      // Agregar nuevo
      newRows = [...rows, newLimit];
    }

    this.setState({
      rows: this.sortAndValidateLimits(newRows),
      modalOpen: false,
      editingIndex: null,
      editingLimit: null
    });
  };

  handleSaveComissions = () => {
    const { rows, tipoMonto } = this.state;
    const { productId } = this.props;

    // Validar que haya al menos un límite configurado
    if (rows.length === 0) {
      errorNotification(
        "Debe configurar al menos un límite de comisión antes de guardar."
      );
      return;
    }

    // Validar que no haya errores en los límites
    if (hasAnyError(rows, tipoMonto)) {
      errorNotification(
        "No se pueden guardar los cambios. Hay errores en los límites configurados. Por favor, corrija los errores marcados en rojo antes de guardar."
      );
      return;
    }

    // Preparar comisiones para el backend
    const cleanValues = rows.map(row => {
      const comision: any = {
        numeroParticipantesHasta: row.numeroParticipantesHasta,
        montoPorParticipante: row.montoPorParticipante,
        montoPorcentajeParticipante: row.montoPorcentajeParticipante
      };
      return comision;
    });

    const puestos = [];
    this.props.catalogPostions.forEach(puesto => {
      const { positionId } = puesto;
      if (this.state.selectedPuestos[positionId]) {
        puestos.push(positionId);
      }
    });

    this.props.saveComissions({
      productoId: parseInt(productId),
      comisiones: cleanValues,
      puestos
    });
  };

  getPositionsSubarrays = (itemsPerPage: number) => {
    if (!!this.props.catalogPostions) {
      return divideArrayInSubArrays(this.props.catalogPostions, itemsPerPage);
    } else {
      return [];
    }
  };

  getPositionsRearranged() {
    const columns = 4;
    const itemsPerPage = Math.floor(
      this.props.catalogPostions.length / columns
    );
    const subArrays = this.getPositionsSubarrays(itemsPerPage);
    const result = [];

    for (let i = 0; i < itemsPerPage; i++) {
      for (let c = 0; c < columns; c++) {
        if (!!subArrays[c][i]) {
          result.push(subArrays[c][i]);
        } else {
          result.push(null);
        }
      }
    }

    return result;
  }

  changeTipoMonto = (newTipo: "PORCENTAJE" | "MONETARIO") => {
    const { rows, tipoMonto } = this.state;

    // Si hay límites y se cambia de PORCENTAJE a MONETARIO, verificar que ningún porcentaje exceda 100
    if (tipoMonto === "PORCENTAJE" && newTipo === "MONETARIO") {
      const hasInvalidPercentage = rows.some(
        row => row.montoPorcentajeParticipante > 100
      );
      if (hasInvalidPercentage) {
        errorNotification(
          "No se puede cambiar a tipo Monetario porque hay porcentajes que exceden el 100%. Por favor, corrija los valores primero."
        );
        return;
      }
    }

    this.setState({ tipoMonto: newTipo });
  };

  onHandleTriggerFormValues = (formValuesSetter: any) => {
    this.setState({ formValuesSetter });
  };

  prevLimiteUsuarios = "";
  prevSinLimite = false;
  onChange = (values: { [key in Inputs]: any }) => {
    // Para no repetir los setState, semejante a componentWillUpdate
    const limiteUsuariosChanged =
      this.prevLimiteUsuarios !== values.limiteUsuarios;
    const sinLimiteChanged = this.prevSinLimite !== values.sinLimite;

    const simulateLimit =
      !!this.state.formValuesSetter &&
      (limiteUsuariosChanged || sinLimiteChanged);

    // Si se requiere, se simula la posición del límite
    if (simulateLimit) {
      this.prevLimiteUsuarios = values.limiteUsuarios;
      this.prevSinLimite = values.sinLimite;
      let calculatedDesde = 1;
      const { rows, editingIndex } = this.state;
      const hastaValue = values.sinLimite
        ? -1
        : parseInt(values.limiteUsuarios || "0");

      // Si se está editando un límite y no es el único
      if (editingIndex !== null && editingIndex > 0) {
        const actualLimit = rows[editingIndex];
        actualLimit.numeroParticipantesHasta = hastaValue;

        // Ordenamiento virtual
        const cpRows = this.sortAndValidateLimits([...rows]);

        // Si el valor es "sin límite", "Desde" se obtiene del último item
        if (hastaValue === -1) {
          calculatedDesde = rows[rows.length - 1].numeroParticipantesHasta;
          calculatedDesde = calculatedDesde == -1 ? 0 : calculatedDesde + 1;
        }

        // Si el valor es uno intermedio, buscar el límite "Hasta" equivalente
        if (hastaValue > 0) {
          const limit = cpRows.find(
            limit => limit.numeroParticipantesHasta === hastaValue
          );
          calculatedDesde = limit
            ? limit.numeroParticipantesDesde
            : values.limiteDesde;
        }
      }

      // Si se está insertando un nuevo límite y no es el único
      if (editingIndex === null && rows.length > 0) {
        // Si el valor es "sin límite", "Desde" se obtiene del último item
        if (hastaValue === -1) {
          calculatedDesde = rows[rows.length - 1].numeroParticipantesHasta;
          calculatedDesde = calculatedDesde == -1 ? 0 : calculatedDesde + 1;
        }

        // Si el valor es uno intermedio, buscar el límite "Hasta" mayor posible
        if (hastaValue > 0) {
          for (let i = rows.length - 1; i >= 0; i--) {
            const participantesHasta = rows[i].numeroParticipantesHasta;

            if (participantesHasta > hastaValue || participantesHasta === -1) {
              calculatedDesde = rows[i].numeroParticipantesDesde;
            } else {
              break;
            }
          }
        }
      }

      this.state.formValuesSetter({
        limiteDesde: calculatedDesde.toString()
      });
    }
  };

  render() {
    const {
      editingLimit,
      editingIndex,
      tipoMonto,
      calculatedDesde
    } = this.state;

    // Preparar valores iniciales para el modal
    const modalInitialValues = editingLimit
      ? {
          limiteDesde: calculatedDesde.toString(),
          limiteUsuarios:
            editingLimit.numeroParticipantesHasta === -1
              ? ""
              : editingLimit.numeroParticipantesHasta.toString(),
          sinLimite: editingLimit.numeroParticipantesHasta === -1,
          monto: (tipoMonto === "PORCENTAJE"
            ? editingLimit.montoPorcentajeParticipante
            : editingLimit.montoPorParticipante
          ).toString()
        }
      : {
          limiteDesde: calculatedDesde.toString(),
          sinLimite: false
        };

    return (
      <div>
        {/* Modal para agregar/editar límites */}
        <FormBuilderModal
          isOpen={this.state.modalOpen}
          title={editingIndex !== null ? "Editar Límite" : "Agregar Límite"}
          message={
            editingIndex !== null
              ? "Modifique los valores del límite de comisión"
              : "Ingrese los valores para el nuevo límite de comisión"
          }
          ok={this.saveLimit}
          cancel={this.closeModal}
          formConfig={formConfigFactory(tipoMonto)}
          values={modalInitialValues}
          size="md"
          triggerFormValues={this.onHandleTriggerFormValues}
          onChange={this.onChange}
        />

        <Container>
          <Row>
            <div className="col-12 mt-3">
              <div className="alert alert-info">
                Las comisiones se ajustarán mensualmente, en el periodo del
                programa asociado al instructor.
              </div>
            </div>
            <Col xs="12">
              <div className="mb-3 mt-3">
                <div className="d-flex align-items-center">
                  <span className="mr-3">Tipo de comisión:</span>
                  <div
                    className="btn-group btn-group-toggle"
                    data-toggle="buttons"
                  >
                    <label
                      id="btn-chk-PORCENTAJE"
                      className={`btn btn-sm btn-outline-primary ${
                        tipoMonto === "PORCENTAJE" ? "active" : ""
                      }`}
                      onClick={() => this.changeTipoMonto("PORCENTAJE")}
                    >
                      <input
                        type="radio"
                        name="tipoMonto"
                        id="PORCENTAJE"
                        checked={tipoMonto === "PORCENTAJE"}
                        readOnly
                      />
                      Porcentaje (%)
                    </label>
                    <UncontrolledTooltip
                      placement="bottom"
                      target="btn-chk-PORCENTAJE"
                    >
                      Comisión por porcentaje
                    </UncontrolledTooltip>
                    <label
                      id="btn-chk-MONETARIO"
                      className={`btn btn-sm btn-outline-primary ${
                        tipoMonto === "MONETARIO" ? "active" : ""
                      }`}
                      onClick={() => this.changeTipoMonto("MONETARIO")}
                    >
                      <input
                        type="radio"
                        name="tipoMonto"
                        id="MONETARIO"
                        checked={tipoMonto === "MONETARIO"}
                        readOnly
                      />
                      Monto fijo ($)
                    </label>
                    <UncontrolledTooltip
                      placement="bottom"
                      target="btn-chk-MONETARIO"
                    >
                      Comisión por monto fijo
                    </UncontrolledTooltip>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <span className="mr-3">Participantes por periodo:</span>
              </div>

              {/* Tabla de límites */}
              <MemoTable
                limits={this.state.rows}
                loading={false}
                tipoMonto={this.state.tipoMonto}
                onEdit={this.openEditModal}
                onDelete={this.deleteLimit}
                onAdd={this.openAddModal}
              />
            </Col>
          </Row>
        </Container>
        <Container className={s.puestos}>
          <Row>
            <Col>
              <p className={`primaryTitle w-100`}>Puestos</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                id={"selectAll"}
                onClick={e => this.selectAll(e, true)}
                color="link"
                className="text-success"
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                id={"unSelectAll"}
                onClick={e => this.selectAll(e, false)}
                color="link"
                className="text-danger"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
              <UncontrolledTooltip placement="left" target={`selectAll`}>
                Selecionar todos
              </UncontrolledTooltip>
              <UncontrolledTooltip placement="right" target={`unSelectAll`}>
                Deseleccionar todos
              </UncontrolledTooltip>
            </Col>
          </Row>
          <Row className={s.checkBoxesContainer}>
            {this.props.catalogPostions &&
              this.getPositionsRearranged().map(puesto => (
                <Col
                  className={s.labelContainer}
                  key={puesto.positionId}
                  xs="3"
                >
                  <FormGroup className={s.label} check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={
                          !!this.state.selectedPuestos[puesto.positionId]
                        }
                        onChange={e => this.checkPuesto(e, puesto.positionId)}
                      />
                      {puesto.name}
                    </Label>
                  </FormGroup>
                </Col>
              ))}
          </Row>
          <Row>
            <div className="col-12 mt-3">
              {this.state.rows.length === 0 && (
                <div className="alert alert-danger">
                  Configure al menos un límite de comisión
                </div>
              )}
              {this.state.rows.length > 0 &&
                hasAnyError(this.state.rows, this.state.tipoMonto) && (
                  <div className="alert alert-danger">
                    Corrija los errores en los límites antes de guardar
                  </div>
                )}
            </div>
            <div className="col mb-5">
              <Button
                size="sm"
                className={s.customAutirizar}
                onClick={this.handleSaveComissions}
                disabled={
                  this.state.rows.length === 0 ||
                  hasAnyError(this.state.rows, this.state.tipoMonto)
                }
                color={
                  this.state.rows.length === 0 ||
                  hasAnyError(this.state.rows, this.state.tipoMonto)
                    ? "secondary"
                    : "primary"
                }
              >
                Guardar
              </Button>
            </div>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withStyles(s)(SalesComissionsSportsProgramConfig);
